import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuthStore } from '../../store';

const DELIVERY_OPTIONS = [
  { v: 'cash', l: '💵 Efectivo' },
  { v: 'transfermovil', l: '📱 Transfermóvil' },
  { v: 'cubacel', l: '📶 Cubacel' },
];

export default function AgentApply() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [status, setStatus] = useState(null); // null | 'pending' | 'approved' | 'rejected'
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    country: 'CU',
    province: '', city: '', coverage_zone: '',
    delivery_methods: ['cash', 'transfermovil'],
    max_tx_per_day: 10,
    daily_capacity_usd: '',
    telegram_username: '',
    motivation: '',
  });

  useEffect(() => {
    api.get('/api/v1/agents/my').then(r => {
      setStatus(r.data.status);
    }).catch(() => {
      setStatus(null);
    }).finally(() => setLoading(false));
  }, []);

  const kycApproved = user?.kyc_status?.toLowerCase() === 'approved';

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleDelivery = (m) => {
    const curr = form.delivery_methods;
    set('delivery_methods', curr.includes(m) ? curr.filter(x => x !== m) : [...curr, m]);
  };

  const handleSubmit = async () => {
    if (!form.province && !form.city) { setError('Indica tu provincia o ciudad de operación'); return; }
    if (form.delivery_methods.length === 0) { setError('Selecciona al menos un método de entrega'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/api/v1/agents/register', {
        ...form,
        max_tx_per_day: parseInt(form.max_tx_per_day) || 10,
        daily_capacity_usd: form.daily_capacity_usd ? parseFloat(form.daily_capacity_usd) : null,
      });
      setStatus('pending');
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Error al enviar la solicitud'));
    } finally { setSaving(false); }
  };

  const inputStyle = { width: '100%', padding: '11px 14px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#faf8f5' };
  const labelStyle = { display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando...</div>;

  if (!kycApproved && !status) return (
    <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center', paddingTop: 32 }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>🪪</div>
      <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 24, margin: '0 0 12px' }}>Verificación requerida</h2>
      <p style={{ fontSize: 14, color: '#7a7060', marginBottom: 24 }}>
        Para ser agente de Guarapo necesitas verificar tu identidad primero.
      </p>
      <a href="/kyc" style={{ display: 'inline-block', padding: '12px 24px', borderRadius: 10, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
        Verificar identidad →
      </a>
    </div>
  );

  // Already applied
  if (status === 'pending') return (
    <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center', paddingTop: 32 }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>⏳</div>
      <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 24, margin: '0 0 12px' }}>Solicitud en revisión</h2>
      <p style={{ fontSize: 14, color: '#7a7060', marginBottom: 24 }}>
        Tu solicitud fue recibida. Un administrador la revisará en 24-48 horas.
      </p>
      <button onClick={() => navigate('/app/dashboard')} style={{ padding: '12px 24px', borderRadius: 10, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        Volver al dashboard
      </button>
    </div>
  );

  if (status === 'approved') return (
    <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center', paddingTop: 32 }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
      <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 24, margin: '0 0 12px' }}>¡Eres agente!</h2>
      <p style={{ fontSize: 14, color: '#7a7060', marginBottom: 24 }}>Tu cuenta de agente está activa.</p>
      <button onClick={() => navigate('/app/agent')} style={{ padding: '12px 24px', borderRadius: 10, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        Ir al panel de agente →
      </button>
    </div>
  );

  if (status === 'rejected') return (
    <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center', paddingTop: 32 }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>❌</div>
      <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 24, margin: '0 0 12px' }}>Solicitud no aprobada</h2>
      <p style={{ fontSize: 14, color: '#7a7060', marginBottom: 24 }}>Contacta soporte@guarapo.app para más información.</p>
      <button onClick={() => navigate('/app/dashboard')} style={{ padding: '12px 24px', borderRadius: 10, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        Volver al dashboard
      </button>
    </div>
  );

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 8px' }}>Convertirme en agente</h1>
        <p style={{ fontSize: 14, color: '#7a7060' }}>
          Conviértete en coordinador local — recibe órdenes y gestiona entregas en tu zona.
        </p>
      </div>

      {/* Benefits */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { icon: '💰', title: 'Ingresos', desc: 'Gana por cada entrega coordinada' },
          { icon: '📱', title: 'App móvil', desc: 'Recibe órdenes en tu teléfono' },
          { icon: '🌍', title: 'Global', desc: 'Opera desde cualquier país' },
        ].map((b, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{b.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{b.title}</div>
            <div style={{ fontSize: 11, color: '#7a7060', lineHeight: 1.4 }}>{b.desc}</div>
          </div>
        ))}
      </div>

      {error && (
        <div style={{ background: '#faeef4', border: '1px solid #f4b8c8', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#9b2c5e' }}>
          ⚠ {error}
        </div>
      )}

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['Zona', 'Capacidad'].map((s, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: step > i + 1 ? '#2d6a4f' : step === i + 1 ? '#c94a1e' : '#e2dbd0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: step >= i + 1 ? '#fff' : '#7a7060', flexShrink: 0 }}>
              {step > i + 1 ? '✓' : i + 1}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: step === i + 1 ? '#0f0d0b' : '#7a7060' }}>{s}</div>
            {i < 1 && <div style={{ flex: 1, height: 2, background: step > i + 1 ? '#2d6a4f' : '#e2dbd0' }} />}
          </div>
        ))}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 16, padding: 24 }}>

        {/* Step 1: Zone */}
        {step === 1 && (
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, marginBottom: 18 }}>Zona de operación</div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>PAÍS DONDE OPERAS</label>
              <select style={inputStyle} value={form.country} onChange={e => set('country', e.target.value)}>
                {[['CU','🇨🇺 Cuba'],['US','🇺🇸 EE.UU.'],['IT','🇮🇹 Italia'],['ES','🇪🇸 España'],['MX','🇲🇽 México'],['OTHER','🌍 Otro']].map(([v,l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>

            {[
              { key: 'province', label: 'PROVINCIA / ESTADO', placeholder: 'La Habana' },
              { key: 'city', label: 'CIUDAD / MUNICIPIO', placeholder: 'Centro Habana' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{f.label}</label>
                <input style={inputStyle} placeholder={f.placeholder}
                  value={form[f.key]} onChange={e => set(f.key, e.target.value)} />
              </div>
            ))}

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>ZONA DE COBERTURA</label>
              <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
                placeholder="Municipios que cubres: Centro Habana, Habana Vieja..."
                value={form.coverage_zone} onChange={e => set('coverage_zone', e.target.value)} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>MÉTODOS DE ENTREGA</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {DELIVERY_OPTIONS.map(m => (
                  <button key={m.v} type="button" onClick={() => toggleDelivery(m.v)}
                    style={{ padding: '8px 14px', borderRadius: 20, border: `2px solid ${form.delivery_methods.includes(m.v) ? '#c94a1e' : '#e2dbd0'}`, background: form.delivery_methods.includes(m.v) ? '#fdf4f0' : '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600, color: form.delivery_methods.includes(m.v) ? '#c94a1e' : '#7a7060' }}>
                    {m.l}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={() => setStep(2)} style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              Continuar →
            </button>
          </div>
        )}

        {/* Step 2: Capacity */}
        {step === 2 && (
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, marginBottom: 18 }}>Capacidad y contacto</div>

            {[
              { key: 'max_tx_per_day', label: 'MÁX. ENTREGAS POR DÍA', placeholder: '10', type: 'number' },
              { key: 'daily_capacity_usd', label: 'CAPACIDAD DIARIA (USD)', placeholder: '500', type: 'number' },
              { key: 'telegram_username', label: 'USUARIO TELEGRAM (opcional)', placeholder: '@tu_usuario' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{f.label}</label>
                <input style={inputStyle} type={f.type || 'text'} placeholder={f.placeholder}
                  value={form[f.key]} onChange={e => set(f.key, e.target.value)} />
              </div>
            ))}

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>¿POR QUÉ QUIERES SER AGENTE? (opcional)</label>
              <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
                placeholder="Tengo experiencia, conozco bien la zona..."
                value={form.motivation} onChange={e => set('motivation', e.target.value)} />
            </div>

            <div style={{ background: '#eaf1fb', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#1a4e8c' }}>
              🔒 Tu solicitud será revisada en 24-48 horas. Te notificaremos por email.
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #e2dbd0', background: '#fff', fontSize: 14, cursor: 'pointer' }}>
                ← Atrás
              </button>
              <button onClick={handleSubmit} disabled={saving}
                style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: saving ? '#e2dbd0' : '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Enviando...' : 'Enviar solicitud →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
